import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categoryLabels } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="font-heading text-xl text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您要查看的漫画不存在或已下架</p>
            <Link to="/" className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const mockChapters = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    number: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    updatedAt: new Date(Date.now() - i * 86400000 * 3).toISOString().split('T')[0],
  }));

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.tags.some((t) => comic.tags.includes(t))))
    .slice(0, 4);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://99manga.com/' },
      { '@type': 'ListItem', position: 2, name: categoryLabels[comic.category], item: `https://99manga.com/category/${comic.category}` },
      { '@type': 'ListItem', position: 3, name: comic.title, item: `https://99manga.com/comic/${comic.id}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    url: `https://99manga.com/comic/${comic.id}`,
    description: comic.description,
    image: comic.cover,
    author: { '@type': 'Organization', name: comic.author },
    publisher: { '@type': 'Organization', name: '99漫画', url: 'https://99manga.com' },
    genre: comic.tags.join(', '),
    about: categoryLabels[comic.category],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <Navbar />
      <main className="pt-16">
        <section className="bg-background-100 py-6 md:py-8">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${comic.category}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">
                {categoryLabels[comic.category]}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700 font-medium line-clamp-1">{comic.title}</span>
            </nav>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-[280px] flex-shrink-0">
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200 sticky top-24">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 99漫画${categoryLabels[comic.category]}详情`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">{comic.title}</h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-600 whitespace-nowrap">作者：{comic.author}</span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-600 whitespace-nowrap">{categoryLabels[comic.category]}</span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
                    comic.status === 'ongoing' ? 'bg-primary-100 text-primary-700' : 'bg-foreground-100 text-foreground-600'
                  }`}>
                    {comic.status === 'ongoing' ? '连载中' : '已完结'}
                  </span>
                </div>

                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-500 text-lg"></i>
                    <span className="font-heading text-xl text-foreground-900">{comic.rating}</span>
                    <span className="text-xs text-foreground-500">/ 10</span>
                  </div>
                  <div className="text-xs text-foreground-500 whitespace-nowrap">
                    <i className="ri-eye-line mr-1"></i>
                    {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 次浏览
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-6 border border-background-200/70">
                  <h3 className="font-medium text-sm text-foreground-900 mb-2">作品简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex gap-3">
                  <button className="flex-1 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-colors whitespace-nowrap text-sm cursor-pointer">
                    <i className="ri-play-circle-line mr-1.5"></i>开始阅读
                  </button>
                  <button className="px-4 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-lg transition-colors cursor-pointer border border-background-200/70">
                    <i className="ri-heart-line text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-xl text-foreground-900 mb-6">章节目录</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {mockChapters.map((ch) => (
                <a
                  key={ch.number}
                  href="#"
                  className="flex items-center justify-between px-4 py-3 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/30 transition-all duration-150 cursor-pointer"
                >
                  <span className="text-sm text-foreground-800 font-medium whitespace-nowrap line-clamp-1">{ch.title}</span>
                  <span className="text-[10px] text-foreground-400 flex-shrink-0 ml-2 whitespace-nowrap">{ch.updatedAt}</span>
                </a>
              ))}
            </div>
            {comic.chapters > 20 && (
              <div className="text-center mt-6">
                <button className="px-6 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                  查看全部 {comic.chapters} 话 <i className="ri-arrow-down-s-line ml-1"></i>
                </button>
              </div>
            )}
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-8 md:py-12 bg-background-50">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="font-heading text-xl text-foreground-900 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                      <img
                        src={rc.cover}
                        alt={rc.title}
                        title={`${rc.title} - 99漫画推荐`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded px-1.5 py-0.5">
                        <i className="ri-star-fill text-accent-400 text-[10px]"></i>
                        <span className="text-background-50 text-[10px] font-medium">{rc.rating}</span>
                      </div>
                    </div>
                    <h3 className="font-medium text-sm text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {rc.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">
                      {categoryLabels[rc.category]} · {rc.chapters}话
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}